import { motion } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { DURATION, EASE_OUT_SOFT, EASE_SPRING } from "../lib/motion";

/**
 * Beyond Sure — Sign-in Success placeholder (v3.4).
 *
 * Placeholder destination at the end of the email → OTP → success flow.
 * In production this would either redirect to the dashboard (window.location)
 * or hand off to a session bootstrap step. In the sandbox we render a
 * brand-aligned confirmation so the founder can click through and SEE the
 * full flow.
 *
 * The same AuthShell guarantees it remains a visual twin of sign-in + OTP.
 */

interface SuccessScreenProps {
  email: string;
  onBack: () => void;
  /** v3.6: optional override for the primary CTA. When provided (App-level
   * flow), the "Open BeyondSure" button advances to onboarding instead of
   * navigating away. Default behavior (no prop) is preserved exactly. */
  onContinue?: () => void;
}

export function SuccessScreen({ email, onBack, onContinue }: SuccessScreenProps) {
  return (
    <AuthShell
      eyebrow="Sign in · Free 7-day trial"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      formTitle="You're in."
      showcase={<PhoneShowcase />}
    >
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.slow, ease: EASE_OUT_SOFT }}
        className="bsure-ui flex flex-col gap-5"
        role="status"
        aria-live="polite"
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: EASE_SPRING, delay: 0.1 }}
          className="flex items-center gap-3"
        >
          <span
            aria-hidden="true"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full"
            style={{
              backgroundColor: "var(--color-brand-deep)",
              color: "var(--color-paper)",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8.5l3 3 7-7"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div className="min-w-0">
            <div className="bsure-prose text-[13px] text-[var(--color-ink-60)]">
              Signed in as
            </div>
            <div className="bsure-ui font-semibold text-[15px] text-[var(--color-ink)] truncate">
              {email}
            </div>
          </div>
        </motion.div>

        <p className="bsure-prose text-[13.5px] leading-[1.6] text-[var(--color-ink-60)]">
          You're now ready to chat with BeyondSure, upload a policy for the
          truth audit, and explore the best plan for your family. The
          dashboard hands off to the live app at{" "}
          <a
            href="/"
            className="text-[var(--color-brand-deep)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors"
          >
            beyondsure.in
          </a>{" "}
          on production.
        </p>

        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            if (onContinue) {
              onContinue();
            } else {
              window.location.assign("/");
            }
          }}
        >
          Open BeyondSure
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="bsure-ui mt-1 text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors self-start"
        >
          ← Sign in with a different email
        </button>
      </motion.div>
    </AuthShell>
  );
}

export default SuccessScreen;
